<template>
  <!-- Mahmoud El farargy, August 24th -->
  <!-- Mustafa Saied El  Sep 1st -->
    <PagePortal
      :key="`store-${storeItem?.id || storeUsername || 'loading'}-${storeItem?.logo || 'no-logo'}`"
      :title="formedPageTitle"
      :description="formedDescription"
      :keywords="formedKeywords"
      :image="storeLogo"
      :imageAlt="storeLogoAltText"
      :instagram="storeItem?.instagram"
      :twitter="storeItem?.twitter"
      class="relative"
      id="storePage"
      pageType='Store'
      :brand="storeItem?.name"
      :isServerSide="true"
      :disableIndexing="shouldDisableIndexing"
    >
    <!-- modals -->
    <!-- share store on social media modal -->
    <ShareLink v-if="storeItem" :label="$t('general.store')" :URLLink="URLLink" :isModalOpen="isShareProductModalOpen"
      :changeModalState="changeShareProductModalState" :title="storeItem.name" :description="storeDescription"
      :quote="storeDescription"
      :hashtags="`Cezma, ${storeItem.name}, ${storeCategoriesAsKeywords}, ${$t('general.store')}, shopping`" />
    <!-- rate store modal -->
    <UserStoreMakeRateModal v-if="ratingData" :ratingData="ratingData" :isModalOpen="isRateStoreModalOpen"
      :isUpdateDataStore="isFetchingStoreData" :changeModalState="changeRateStoreModalState"
      :directUserToLoginPage="directUserToLoginPage" :refreshStoreData="getComments" :editedRating="editedRating" />
    <!-- store comments modal -->
    <UserStoreCommentsModal v-if="ratingData" :ratingData="ratingData" :isModalOpen="isStoreCommentsModalOpen"
      :isUpdateDataStore="isFetchingStoreData" :changeModalState="changeStoreCommentsModalState"
      :directUserToLoginPage="directUserToLoginPage" :refreshStoreData="getComments" :onEditRatingOpenModal="onEditRatingOpenModal" />
    <!-- PAGE START -->
    <!-- outer container -->
    <!-- if the store is not available/empty, show this -->
     <div v-if="pageLoading" class="h-[90vh] flex items-center justify-center text-[30px] text-primary">
      {{ $t('general.loading') }}
     </div>
    <div v-else-if="(!storeItem || !storeId || Object.keys(storeItem || {}).length < 1) && !pageLoading "
      class="bg-[#ffffff00] h-[60vh] mt-10 md:mt-0 mb-20 border-gray-200 flex flex-col items-center justify-center px-4 rounded-lg">
      <p class="text-6xl md:text-7xl lg:text-9xl font-bold tracking-wider text-gray-300">
        <svg aria-hidden="true" class="mx-auto mb-4 text-gray-400 w-14 h-14" fill="none" stroke="currentColor"
          viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </p>
      <p class="text-[18px] md:text-2xl lg:text-2xl font-bold text-gray-500 mt-4 text-center">
        {{ $t("general.no_store_available") }}
      </p>
      <button @click="$router.push('/')"
        class="flex items-center space-x-2 text-[14px] md:text-[16px] bg-blue-600 hover:bg-blue-700 text-gray-100 px-4 py-2 mt-6 rounded transition duration-150"
        :title="$t('general.back_home')">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd"
            d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z"
            clip-rule="evenodd"></path>
        </svg>
        <span>{{ $t("general.back_home") }}</span>
      </button>
    </div>
    <!-- else, show the returned stored data -->
    <div v-else class="relative text-[#000] overflow-hidden">
      <div class="absolute inset-0 z-[-1]" :style="containerStyleValue" />

      <div class="store-container page__inner">
        <!-- inner container -->
        <div :class="`relative py-2 pb-10 lg:pb-12 xl:pb-20 mt-0 pt-0 ${storeItem.background_image ? 'store__inner--container' : ''} ${
            storeItem.background_image
              ? 'bg-[#ffffffd9]'
              : 'bg-[#f1f1f1a6]'
          } page__inner w-full flex justify-center`">
          <div class="store-inner w-full page__inner">
            <Breadcrumb :links="breadcrumbLinks" :removePadding="true" class="pt-5 pb-4 sm:pb-6 md:py-8" />
            <!-- top area -->
            <div class="flex justify-end"></div>
            <!-- store info -->
            <StoreHeader :storeItem="storeItem" :triggerContactPixelEvent="triggerContactPixelEvent"
              :changeShareProductModalState="changeShareProductModalState" :toggleFollowingState="toggleFollowingState"
              :handleViewingStoreInteractionModal="handleViewingStoreInteractionModal"
              :onStoreImageError="onStoreImageError" :finalizedNameKey="finalizedNameKey"
              :toggleBellNotification="toggleBellNotification" :isMuted="isMuted"
              :isTogglingMuteNotification="isTogglingMuteNotification" :storeId="storeId"
              :storeDescription="storeDescription" :followed="isFollowing" :followingInProgress="followingInProgress"
              :storeCurrentURL="storeCurrentURL" :storeCategoriesList="storeCategoriesList" />
            <GoogleAdsBanner v-if="!shouldHideGoogleAdsBanner" />
            
            <!-- #NEWS SECTION -->
            <div v-if="isNews" class="mt-5 md:mt-7 lg:mt-10">
              <StoreNewsList :list="allNewsItemsList" :isLoading="isLoadingNews" :isLoadingMore="isLoadingMoreNews"
                :hasReachedFullPages="allNewsData && allNewsData.hasReachedFullPages"
                :loadMoreData="loadMoreNewsData" />
            </div>
            <!-- #STORE SECTION -->
            <div v-else>
              <!-- store contact info -->
              <StoreContactInfo class="mt-5 md:mt-9 xl:mt-12" :storeItem="storeItem"
                :finalizedNameKey="finalizedNameKey" :storeCurrentURL="storeCurrentURL"
                :storeCategoriesList="storeCategoriesList" />
              <!-- store location & description -->

              <!-- Store branches -->
              <HomePopularStores class="mt-9" :title="$t('user.store_branches')" v-if="storeBranchesList.length > 0"
                :storesList="storeBranchesList" :fromSingleStorePage="true" :storesType="`branch`" :designId="2" />

              <!-- Related stores -->
              <HomePopularStores class="mt-9" :title="$t('user.store.related_stores')"
                :fromStorePage="true"
                v-if="relatedStoresList.length > 0" :storesList="relatedStoresList" :designId="2" />
              <section class="flex flex-wrap mt-1 sm:mt-6 gap-y-0 md:flex-nowrap md:gap-y-4 md:gap-x-7">
                <!-- Store ratings & comments -->
                <StoreRatingSection class="w-full mt-6 lg:w-1/2" :storeItem="storeItem" :isLoading="isLoadingComments"
                  :handleViewingStoreInteractionModal="handleViewingStoreInteractionModal"
                  :firstComments="commentsFirstPage"
                  :handleLikeClick="handleLikeClick" :onEditRatingOpenModal="onEditRatingOpenModal" />
                <!-- Store News -->
                <StoreNewsSection class="w-full mt-6 lg:w-1/2"
                  :list="newsFirstPage" :storeUsername="storeItem && storeItem.username"
                  :newsLength="allNewsItemsList && allNewsItemsList.length"
                  :isLoading="isLoadingNews"
                />
              </section>
              <!-- Exclusive Offers -->
              <StoreExclusiveOffersSection :list="exclusiveOffersFirstPage" :storeId="storeId" :isLoading="isLoadingExclusiveOffers" @saveExclusiveOfferPosition="saveExclusiveOfferPosition" />

              <!-- Store products -->
              <!-- when loading -->
              <LoadingProductsList v-if="isFetchingStoreData" />
              <!-- when loaded -->
              <div class="w-full mt-4 xl:mt-7">
                <span class="flex flex-nowrap gap-2">
                  <UserStoreSectionTitle :title="$t('user.store_products')" />
                </span>
              </div>

              <!-- Search input moved here, above tabs -->
              <div v-if="!disableProductFilterAndSearchInputs" class="w-full flex justify-between md:items-center md:flex-row flex-col gap-2 gap-y-3 lg:gap-5 xl:gap-7 mt-4 mb-4">
                <!-- filter by search -->
                <!-- search bar -->
                <form @submit.prevent="handleSearch"
                  :class="`${isSearchBarFocused ? ' border-primary' : 'border-transparent'} border ${disableProductFilterAndSearchInputs ? 'disabled' : ''} w-full md:max-w-[34.25rem] md:self-end flex flex-nowrap bg-white rounded-[0.625rem] overflow-hidden flex-1`.trim()">
                  <button type="submit" :disabled="disableProductFilterAndSearchInputs || !searchInput"
                    class="!outline-none rtl:pr-4 rtl:pl-2 pl-4 pr-2 rtl:lg:pr-6 rtl:lg:pl-1 lg:pl-6 lg:pr-1"
                    aria-label="search stores">
                    <img src="@/assets/icons/search.svg" alt width="23" height="23" />
                  </button>
                  <input type="search" v-model.trim="searchInput" :disabled="disableProductFilterAndSearchInputs"
                    @input="onProductsSearchChange"
                    class="py-3 w-full border-none focus:outline-none focus:ring-0 rtl:pr-1 rtl:pl-3 pl-1 pr-3 placeholder:text-black placeholder:text-[0.875rem] caret-primary"
                    :placeholder="$t('user.store.search')" @focus="() => isSearchBarFocused = true"
                    @blur="() => isSearchBarFocused = false" />
                </form>
                <div>
                  <div id="productType" class="flex gap-2" v-if="storeItem.products">
                    <ProductTypeItem v-for="typeOption in productTypeOptions" :key="typeOption.value"
                      :typeOption="typeOption" :selectedProductTypeId="selectedProductTypeId"
                      @onProductTypeFilterChange="onProductTypeFilterChange" />
                  </div>
                </div>
              </div>

              <!-- Tabs -->
              <div class="flex justify-between items-center mb-4 border-b">
                <div class="flex gap-4">
                  <button 
                    @click="switchToProductsTab"
                    :class="`px-4 py-2 ${activeTab === 'products' ? 'outline-none text-primary border-b-2 border-primary' : 'text-gray-500'}`"
                  >
                    {{ $t('general.products') }}
                  </button>
                  <button 
                    @click="switchToCategoriesTab"
                    :class="`px-4 py-2 ${activeTab === 'categories' ? 'outline-none text-primary border-b-2 border-primary' : 'text-gray-500'}`"
                  >
                    {{ $t('general.categories') }}
                  </button>
                </div>
                
                <!-- Mobile filter button - only visible on mobile -->
                <button 
                  @click="openFilterModal"
                  class="md:hidden px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md flex items-center gap-1.5 transition-colors text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M4 21v-7m0-4V3"/>
                    <path d="M12 21v-9m0-4V3"/>
                    <path d="M20 21v-5m0-4V3"/>
                    <path d="M4 7h.01"/>
                    <path d="M12 12h.01"/>
                    <path d="M20 16h.01"/>
                  </svg>
                  {{ $t('general.filter') }}
                </button>
              </div>

              <!-- Sorting selector - show only when products tab is active -->
              <div v-if="activeTab === 'products'" class="flex justify-between mb-4">
                <div>
                  <ListSortingSelector
                    @onSelect="onSortChange"
                    :isProcessing="isLoadingProducts"
                    :selectedItemId="selectedSortBy"
                    :sortingOptions="sortByOptions"
                    :designId="3"
                  />
                </div>
              </div>

              <!-- Categories slider - show only when categories tab is active -->
              <div v-if="activeTab === 'categories'" class="mb-4">
                <ItemsSlider v-if="storeCategoriesList && storeCategoriesList.length > 0" 
                  appendedClass="items-baseline" :loop="true" autoPlaySpeed="1500">
                  <CategoryItem
                    v-for="category in [{
                      id: 'all', 
                      name_ar: $t('general.all'), 
                      name_en: $t('general.all'), 
                      image: defaultCezmaIcon
                    }, ...storeCategoriesList]" 
                    :key="category.id" 
                    :category="category"
                    :asLink="false"
                    :designId="2"
                    @itemClick="(category) => onCategoryFilterChange({sortBy: category.id})"
                    :selectedItemId="selectedItemId"
                  />
                </ItemsSlider>
              </div>

              <section>
                <!-- Filter and Products Layout -->
                <div class="flex flex-col md:flex-row gap-6 mt-4">
                  <!-- Products List - takes remaining space -->
                  <div class="md:w-3/4" :class="$i18n.locale === 'ar' ? 'md:order-2' : 'md:order-1'">
                    <!-- shows div when the product items are empty -->
                    <div v-if="storeProducts && storeProducts.length <=0">
                      <!-- shows while filtering / searching -->
                      <div v-if="isFetchingStoreData" class="grid grid-cols-1 md:grid-cols-2">
                        <div class="p-4 text-sm text-gray-800 rounded-lg bg-gray-50 dark:bg-gray-800 dark:text-gray-300"
                          role="alert">
                          {{ $t('general.loading')}}
                        </div>
                      </div>
                      <!-- shows when there are no results found -->
                      <div v-else-if="noResultsFound" class="grid grid-cols-1 md:grid-cols-2">
                        <div class="p-4 text-sm text-gray-800 rounded-lg bg-gray-50 dark:bg-gray-800 dark:text-gray-300"
                          role="alert">
                          {{ $t('general.no_results') }}
                        </div>
                      </div>
                    </div>
                    <!-- otherwise, show the resulted product items -->
                    <div v-else class="store-products-list-wrapper">
                      <HomeTrendyProducts :class="isLoadingProducts ? 'disabled' : ''"
                        :portalPage="setPortalPage"
                        :hasReachedFullPages="hasProductsReachedMaxPages" :isLoading="isFetchingStoreData"
                        :isLoadingMore="isLoadingMoreProducts" :productsList="storeProducts"
                        :withPin="true"
                        :fromFilterProducts="false"
                        :loadMoreProducts="handleLoadingMoreProducts" :isStorePage="true" title="" :cardDesignId="2"
                        @saveProductCardPosition="saveProductCardPosition" />
                    </div>
                  </div>
                  
                  <!-- Filter Sidebar - always visible regardless of product results -->
                  <div class="hidden md:block md:w-1/4 bg-white p-5 rounded-lg shadow-sm border border-gray-100 transition-all sticky top-24 max-h-[calc(100vh-140px)] overflow-y-auto custom-scrollbar" 
                       :class="$i18n.locale === 'ar' ? 'md:order-1' : 'md:order-2'">
                    <h2 class="text-lg font-medium mb-5 text-gray-800 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-primary" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clip-rule="evenodd" />
                      </svg>
                      {{ $t('general.filter_options') }}
                    </h2>
                    
                    <!-- Main Categories -->
                    <div class="mb-6">
                      <h3 class="font-medium mb-3 text-gray-700 border-b pb-2 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" />
                        </svg>
                        {{ $t('general.categories') }}
                      </h3>
                      <div class="space-y-3 pl-2">
                        <div 
                          v-for="category in [{
                            id: 'all', 
                            name_ar: $t('general.all'), 
                            name_en: $t('general.all')
                          }, ...storeCategoriesList]" 
                          :key="category.id"
                          class="flex items-center py-1.5"
                        >
                          <input 
                            type="radio" 
                            :id="`modal-category-${category.id}`" 
                            name="modal-category" 
                            :value="category.id"
                            :checked="selectedCategoryId === category.id"
                            @change="onCategoryFilterChange({sortBy: category.id})"
                            class="form-radio text-primary focus:ring-primary mr-2 rtl:ml-2 rtl:mr-0"
                          />
                          <label :for="`modal-category-${category.id}`" class="cursor-pointer text-sm hover:text-primary transition-colors flex-1">
                            {{ category[finalizedNameKey] }}
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Subcategories (if available) -->
                    <div class="mb-6" v-if="currentSubCategories && currentSubCategories.length > 0">
                      <h3 class="font-medium mb-3 text-gray-700 border-b pb-2 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                        {{ $t('general.subcategories') }}
                      </h3>
                      <div class="bg-gray-50 rounded-lg p-2">
                        <select 
                          class="w-full p-2.5 border rounded-md text-sm focus:border-primary focus:ring-1 focus:ring-primary bg-white"
                          @change="onSubCategoryFilterChange($event.target.options[$event.target.selectedIndex].subCategory)"
                        >
                          <option :value="null">{{ $t('general.all') }}</option>
                          <option 
                            v-for="subCategory in currentSubCategories" 
                            :key="subCategory.id"
                            :value="subCategory.id"
                            :selected="selectedSubCategoryId === subCategory.id"
                            :subCategory="subCategory"
                          >
                            {{ subCategory[finalizedNameKey] }}
                          </option>
                        </select>
                      </div>
                    </div>
                    
                    <!-- Product Type -->
                    <div class="mb-6">
                      <h3 class="font-medium mb-3 text-gray-700 border-b pb-2 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        {{ $t('general.product_type') }}
                      </h3>
                      <div class="grid grid-cols-2 gap-2 pl-2">
                        <div 
                          v-for="typeOption in productTypeOptions" 
                          :key="typeOption.value"
                          class="flex items-center py-1.5"
                        >
                          <input 
                            type="radio" 
                            :id="`modal-type-${typeOption.value}`" 
                            name="modal-productType" 
                            :value="typeOption.value"
                            :checked="selectedProductTypeId === typeOption.value"
                            @change="onProductTypeFilterChange(typeOption.value)"
                            class="form-radio text-primary focus:ring-primary mr-2 rtl:ml-2 rtl:mr-0"
                          />
                          <label :for="`modal-type-${typeOption.value}`" class="cursor-pointer text-sm hover:text-primary transition-colors">
                            {{ typeOption.text }}
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Sort By in sidebar -->
                    <div class="mb-6">
                      <h3 class="font-medium mb-3 text-gray-700 border-b pb-2 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                        </svg>
                        {{ $t('general.sort_by') }}
                      </h3>
                      <div class="space-y-1.5">
                        <div 
                          v-for="option in filterSortOptions" 
                          :key="option.value"
                          :class="`flex items-center py-1.5 px-2 rounded-md transition-colors ${selectedSortBy === option.value ? 'bg-blue-50' : 'hover:bg-gray-50'}`"
                        >
                          <input 
                            type="radio" 
                            :id="`sort-${option.value}`" 
                            name="sortBy" 
                            :value="option.value"
                            :checked="selectedSortBy === option.value"
                            @change="onSortChange({sortBy: option.value})"
                            class="form-radio text-primary focus:ring-primary mr-2 rtl:ml-2 rtl:mr-0"
                          />
                          <label :for="`sort-${option.value}`" class="cursor-pointer text-sm hover:text-primary transition-colors flex-1">
                            {{ option.text }}
                          </label>
                        </div>
                      </div>
                    </div>
                    <!-- Price Range Filter - Add this section -->
                    <div class="mb-6">
                      <h3 class="font-medium mb-3 text-gray-700 border-b pb-2 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {{ $t('general.price_range') }}
                      </h3>
                      <div class="space-y-3 mt-3">
                        <div class="flex flex-col">
                          <label class="text-sm text-gray-600 mb-1">{{ $t('general.min_price') }}</label>
                          <input 
                            type="text" 
                            v-model="displayMinPrice" 
                            class="w-full p-2 border rounded-md text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                            @blur="formatPriceInputs"
                            @keyup.enter="applyPriceFilter"
                          />
                        </div>
                        <div class="flex flex-col">
                          <label class="text-sm text-gray-600 mb-1">{{ $t('general.max_price') }}</label>
                          <input 
                            type="text" 
                            v-model="displayMaxPrice" 
                            class="w-full p-2 border rounded-md text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                            @blur="formatPriceInputs"
                            @keyup.enter="applyPriceFilter"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <!-- Active Filters Summary -->
                    <div class="mb-6" v-if="hasActiveFilters">
                      <h3 class="font-medium mb-3 text-gray-700 border-b pb-2 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        {{ $t('general.active_filters') }}
                      </h3>
                      <div class="flex flex-wrap gap-2 mt-2">
                        <div v-if="selectedCategoryId !== 'all'" class="bg-blue-50 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                          {{ getCategoryName(selectedCategoryId) }}
                          <button @click="onCategoryFilterChange({sortBy: 'all'})" class="ml-1 focus:outline-none">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        <div v-if="selectedSubCategoryId" class="bg-blue-50 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                          {{ getSubCategoryName(selectedSubCategoryId) }}
                          <button @click="selectedSubCategoryId = null; getProducts({selectedSubCategoryId: null})" class="ml-1 focus:outline-none">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        <div v-if="selectedProductTypeId !== 'all'" class="bg-blue-50 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                          {{ getProductTypeName(selectedProductTypeId) }}
                          <button @click="onProductTypeFilterChange('all')" class="ml-1 focus:outline-none">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        <div v-if="selectedSortBy !== 'all'" class="bg-blue-50 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                          {{ getSortByName(selectedSortBy) }}
                          <button @click="onSortChange({sortBy: 'all'})" class="ml-1 focus:outline-none">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        <div v-if="searchInput && searchInput.trim() !== ''" class="bg-blue-50 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                          "{{ searchInput }}"
                          <button @click="searchInput = ''; handleSearch()" class="ml-1 focus:outline-none">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        <!-- Price filter tag -->
                        <div v-if="hasPriceFilter" class="bg-blue-50 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                          {{ getPriceFilterLabel() }}
                          <button @click="clearPriceFilter" class="ml-1 focus:outline-none">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Reset Button -->
                    <button 
                      @click="resetFilters"
                      v-if="hasActiveFilters"
                      class="w-full py-2.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-colors font-medium hover:shadow-sm flex items-center justify-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      {{ $t('general.reset_all_filters') }}
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Filter Modal -->
    <div v-if="isFilterModalOpen" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div class="flex items-end justify-center min-h-screen text-center sm:block sm:p-0">
        <!-- Background overlay with blur effect -->
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-filter backdrop-blur-sm transition-opacity" @click="closeFilterModal"></div>

        <!-- Modal panel - full height on mobile, slides up from bottom -->
        <div class="inline-block align-bottom bg-white rounded-t-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full max-w-lg mx-auto h-[85vh] sm:h-auto sm:rounded-lg">
          <!-- Header with sticky position -->
          <div class="sticky top-0 bg-white px-4 pt-5 pb-4 border-b z-10">
            <div class="flex justify-between items-center">
              <h3 class="text-lg font-medium text-gray-900">{{ $t('general.filter_options') }}</h3>
              <button @click="closeFilterModal" class="text-gray-400 hover:text-gray-500 focus:outline-none">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <!-- Filter content - scrollable area -->
          <div class="px-4 py-3 space-y-6 overflow-y-auto max-h-[calc(85vh-130px)] sm:max-h-[60vh]">
            <!-- Active Filters Summary - Added to mobile modal -->
            <div class="mb-2" v-if="hasActiveFilters">
              <h3 class="font-medium mb-3 text-gray-700 border-b pb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                {{ $t('general.active_filters') }}
              </h3>
              <div class="flex flex-wrap gap-2 mt-2">
                <div v-if="selectedCategoryId !== 'all'" class="bg-blue-50 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                  {{ getCategoryName(selectedCategoryId) }}
                  <button @click="onCategoryFilterChange({sortBy: 'all'})" class="ml-1 focus:outline-none">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
                <div v-if="selectedSubCategoryId" class="bg-blue-50 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                  {{ getSubCategoryName(selectedSubCategoryId) }}
                  <button @click="selectedSubCategoryId = null; getProducts({selectedSubCategoryId: null})" class="ml-1 focus:outline-none">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
                <div v-if="selectedProductTypeId !== 'all'" class="bg-blue-50 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                  {{ getProductTypeName(selectedProductTypeId) }}
                  <button @click="onProductTypeFilterChange('all')" class="ml-1 focus:outline-none">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
                <div v-if="selectedSortBy !== 'all'" class="bg-blue-50 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                  {{ getSortByName(selectedSortBy) }}
                  <button @click="onSortChange({sortBy: 'all'})" class="ml-1 focus:outline-none">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
                <div v-if="searchInput && searchInput.trim() !== ''" class="bg-blue-50 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                  "{{ searchInput }}"
                  <button @click="searchInput = ''; handleSearch()" class="ml-1 focus:outline-none">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
                <!-- Price filter tag -->
                <div v-if="hasPriceFilter" class="bg-blue-50 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                  {{ getPriceFilterLabel() }}
                  <button @click="clearPriceFilter" class="ml-1 focus:outline-none">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Main Categories -->
            <div class="mb-2">
              <h3 class="font-medium mb-3 text-gray-700 border-b pb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" />
                </svg>
                {{ $t('general.categories') }}
              </h3>
              <div class="space-y-3 pl-2">
                <div 
                  v-for="category in [{
                    id: 'all', 
                    name_ar: $t('general.all'), 
                    name_en: $t('general.all')
                  }, ...storeCategoriesList]" 
                  :key="category.id"
                  class="flex items-center py-1.5"
                >
                  <input 
                    type="radio" 
                    :id="`modal-category-${category.id}`" 
                    name="modal-category" 
                    :value="category.id"
                    :checked="selectedCategoryId === category.id"
                    @change="onCategoryFilterChange({sortBy: category.id})"
                    class="form-radio text-primary focus:ring-primary mr-2 rtl:ml-2 rtl:mr-0"
                  />
                  <label :for="`modal-category-${category.id}`" class="cursor-pointer text-sm hover:text-primary transition-colors flex-1">
                    {{ category[finalizedNameKey] }}
                  </label>
                </div>
              </div>
            </div>
            
            <!-- Subcategories (if available) -->
            <div class="mb-2" v-if="currentSubCategories && currentSubCategories.length > 0">
              <h3 class="font-medium mb-3 text-gray-700 border-b pb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
                {{ $t('general.subcategories') }}
              </h3>
              <div class="bg-gray-50 rounded-lg p-2">
                <select 
                  class="w-full p-2.5 border rounded-md text-sm focus:border-primary focus:ring-1 focus:ring-primary bg-white"
                  @change="onSubCategoryFilterChange($event.target.options[$event.target.selectedIndex].subCategory)"
                >
                  <option :value="null">{{ $t('general.all') }}</option>
                  <option 
                    v-for="subCategory in currentSubCategories" 
                    :key="subCategory.id"
                    :value="subCategory.id"
                    :selected="selectedSubCategoryId === subCategory.id"
                    :subCategory="subCategory"
                  >
                    {{ subCategory[finalizedNameKey] }}
                  </option>
                </select>
              </div>
            </div>
            
            <!-- Product Type -->
            <div class="mb-2">
              <h3 class="font-medium mb-3 text-gray-700 border-b pb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                {{ $t('general.product_type') }}
              </h3>
              <div class="grid grid-cols-2 gap-2 pl-2">
                <div 
                  v-for="typeOption in productTypeOptions" 
                  :key="typeOption.value"
                  class="flex items-center py-1.5"
                >
                  <input 
                    type="radio" 
                    :id="`modal-type-${typeOption.value}`" 
                    name="modal-productType" 
                    :value="typeOption.value"
                    :checked="selectedProductTypeId === typeOption.value"
                    @change="onProductTypeFilterChange(typeOption.value)"
                    class="form-radio text-primary focus:ring-primary mr-2 rtl:ml-2 rtl:mr-0"
                  />
                  <label :for="`modal-type-${typeOption.value}`" class="cursor-pointer text-sm hover:text-primary transition-colors">
                    {{ typeOption.text }}
                  </label>
                </div>
              </div>
            </div>
            
            <!-- Sort By in mobile modal -->
            <div class="mb-2">
              <h3 class="font-medium mb-3 text-gray-700 border-b pb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                </svg>
                {{ $t('general.sort_by') }}
              </h3>
              <div class="grid grid-cols-2 gap-2 pl-2">
                <div 
                  v-for="option in filterSortOptions" 
                  :key="option.value"
                  class="flex items-center py-1.5"
                >
                  <input 
                    type="radio" 
                    :id="`modal-sort-${option.value}`" 
                    name="modal-sortBy" 
                    :value="option.value"
                    :checked="selectedSortBy === option.value"
                    @change="onSortChange({sortBy: option.value})"
                    class="form-radio text-primary focus:ring-primary mr-2 rtl:ml-2 rtl:mr-0"
                  />
                  <label :for="`modal-sort-${option.value}`" class="cursor-pointer text-sm hover:text-primary transition-colors">
                    {{ option.text }}
                  </label>
                </div>
              </div>
            </div>
             <!-- Price Range Filter - Add this section to mobile modal -->
             <div class="mb-2">
              <h3 class="font-medium mb-3 text-gray-700 border-b pb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {{ $t('general.price_range') }}
              </h3>
              <div class="grid grid-cols-2 gap-3 mt-2">
                <div class="flex flex-col">
                  <label class="text-sm text-gray-600 mb-1">{{ $t('general.min_price') }}</label>
                  <input 
                    type="text" 
                    v-model="displayMinPrice" 
                    class="w-full p-2 border rounded-md text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                    @blur="formatPriceInputs"
                    @keyup.enter="applyFiltersAndCloseModal"
                  />
                </div>
                <div class="flex flex-col">
                  <label class="text-sm text-gray-600 mb-1">{{ $t('general.max_price') }}</label>
                  <input 
                    type="text" 
                    v-model="displayMaxPrice" 
                    class="w-full p-2 border rounded-md text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                    @blur="formatPriceInputs"
                    @keyup.enter="applyFiltersAndCloseModal"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <!-- Footer with action buttons - sticky position -->
          <div class="sticky bottom-0 bg-gray-50 px-4 py-3 flex flex-row gap-2 border-t">
            <button 
              @click="resetFiltersAndCloseModal"
              class="flex-1 inline-flex justify-center items-center rounded-md border border-gray-300 shadow-sm px-4 py-2.5 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {{ $t('general.reset') }}
            </button>
            <button 
              @click="applyFiltersAndCloseModal"
              class="flex-1 inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2.5 bg-primary text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              {{ $t('general.apply') }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </PagePortal>
</template>

<script>
import {
  HomePopularStores,
  HomeTrendyProducts,
  PagePortal,
  Breadcrumb,
  ShareLink,
} from "@/components";
import { useGlobalStore } from "@/store/Modules/global";
import { mapState } from "pinia";
import {
  ROUTESNAMES,
  MAINROLES,
  APIREQUESTS,
  isEmpty,
  arabizeNumbers,
  decodeString,
  buildStoreLink,
  checkPaginationEnd,
  constructQueryString,
} from "@/helpers";
import makeServerRequest from "@/composables/makeServerRequest";
import StoreHeader from "@/components/user/store/StoreHeader.vue";
import LoadingProductsList from "@/components/loadingSections/ProductsList.vue";
import StoreContactInfo from "@/components/user/store/StoreContactInfo.vue";
import ProductTypeItem from "@/components/user/store/ProductTypeItem.vue";
import defaultStoreImg from "@/assets/images/shopping-lo.png";
import StoreRatingSection from "@/components/user/store/StoreRatingSection.vue";
import StoreExclusiveOffersSection from "@/components/user/store/StoreExclusiveOffersSection.vue";
import StoreNewsSection from "@/components/user/store/StoreNewsSection.vue";
import StoreNewsList from "@/components/user/store/StoreNewsList.vue";
import mapImg from "@/assets/store/cropped-google_map-1.png";
import phoneIcon from "@/assets/store/call.svg";
import ListSortingSelector from "@/components/user/general/ListSortingSelector.vue";
import { navigateTo } from '#app';
import ItemsSlider from "@/components/generic/ItemsSlider.vue";
import CategoryItem from "@/components/user/homepage/CategoryItem.vue";
import GoogleAdsBanner from '@/components/generic/GoogleAdsBanner.vue';
import defaultCezmaIcon from "@/assets/icons/cezma_default_icon.png";
import { safeProductListCache } from "@/utils/productListCache";
export default {
  name: "store",
//   async setup() {
//     const global = useGlobalStore();
//     const usedCookieLanguage = useCookie("localStorageCopy")?.value?.language;
//     definePageMeta({
//       layout: "user",
//     });
//     const route = useRoute();
//     const storeUsername = decodeString(route?.params?.username);
//     const savedStoresData = global?.savedStoresData;
//     const updateStoresData = global?.updateStoresData;
//     let finalizedResponseObject = {};

//     if (
//       Object.keys(savedStoresData || {})?.length &&
//       savedStoresData[storeUsername] &&
//       Object.keys(savedStoresData[storeUsername])
//     ) {
//       //if data inside pinia exists, get data from pinia
//       finalizedResponseObject = savedStoresData[storeUsername];
//     } else {
//       // otherwise, make a new request
//       const passwordCookie = useCookie('storePassword');
//       // check if store has password 
//       const hasPassword = storeUsername == passwordCookie.value.name
//       const password = passwordCookie.value?.password




//       const { data } = await makeServerRequest({url:`${APIREQUESTS.user.getStore.url}/${storeUsername}${hasPassword ? `?password=${password}`: ''}`, includeUserToken: true, includeUID: true});
      
     
//       finalizedResponseObject = data.value?.data;
//     }

//     return {
//       storeInitialResponse: finalizedResponseObject || {},
//       updateStoresData,
//       usedCookieLanguage,
//       storeUsername,
//     };
//   },
 
async setup() {
  // Get necessary instances and data
  const global = useGlobalStore();
  const usedCookieLanguage = useCookie("localStorageCopy")?.value?.language || 'ar';
  
  definePageMeta({
    layout: "user",
  });

  const route = useRoute();
  const storeUsername = decodeString(route?.params?.username);

  // Retrieve password from cookie
  const passwordCookie = useCookie('storePassword');
  const hasPassword = storeUsername === passwordCookie.value?.name;
  const password = passwordCookie.value?.password;

  // Access global store functions
  const savedStoresData = global?.savedStoresData;
  const updateStoresData = global?.updateStoresData;

  // Data variable to hold the final store data
  let finalizedResponseObject = {};

  // Check if saved store data matches the current store username
  if (
    Object.keys(savedStoresData || {})?.length &&
    savedStoresData[storeUsername] &&
    Object.keys(savedStoresData[storeUsername])
  ) {
    finalizedResponseObject = savedStoresData[storeUsername];
  } else {
    // Make a new request if store data doesn't match
    try {
      const { data, error } = await makeServerRequest({
        url: `${APIREQUESTS.user.getStore.url}/${storeUsername}${hasPassword ? `?password=${password}` : ''}`,
        includeUserToken: true,
        includeUID: true,
      });

      // Check for errors or password protection
      if (error?.value || !data?.value) {
        throw new Error('Failed to fetch store data');
      }

      // Check error code to redirect to password page if needed
      const responseData = data.value;
      
      // If API returns error code 1009, redirect to password page
      if (responseData?.error_code === 1009) {
        if (process.server) {
          throw createError({
            statusCode: 302,
            statusMessage: 'Found',
            headers: {
              Location: `/store/store-password/${storeUsername}`
            }
          });
        } else {
          await navigateTo(`/store/store-password/${storeUsername}`);
          return; // Exit early to prevent further processing
        }
      }

      // Handle API errors - show "Store not found" for any error
      if (!responseData?.status) {
        // For any API error, show the "Store not found" section
        // Only redirect to password page if we specifically have error_code 1009
        if (responseData?.error_code === 1009) {
          if (process.server) {
            throw createError({
              statusCode: 302,
              statusMessage: 'Found',
              headers: {
                Location: `/store/store-password/${storeUsername}`
              }
            });
          } else {
            await navigateTo(`/store/store-password/${storeUsername}`);
            return; // Exit early to prevent further processing
          }
        } else {
          // Any other error (store not found, server error, etc.) - show "Store not found" section
          finalizedResponseObject = { error: "Store not found" };
        }
      }

      // Assign and update the global storage with fetched data
      finalizedResponseObject = responseData?.data;
      
      // Remove the store password from cookies after successful use
      if (hasPassword && finalizedResponseObject) {
        passwordCookie.value = null;
      }
      
    } catch (err) {
      console.error('Error fetching store data:', err);
      
      // If it's a navigation error (already redirected), don't set error data
      if (err?.statusCode === 302 || err?.message?.includes('redirect')) {
        finalizedResponseObject = {};
      } else {
        finalizedResponseObject = { error: "Failed to fetch store data" };
      }
    }
  }

  // Return all data needed by the template
  return {
    storeInitialResponse: finalizedResponseObject || {},
    updateStoresData,
    usedCookieLanguage,
    storeUsername,
  };
},

data() {
    return {
      defaultCezmaIcon,
      reachedEnd: false,
      activeTab: 'products',
      storePassword: null,
      pageLoading: true,
      APIREQUESTS,
      defaultStoreImg,
      phoneIcon,
      mapImg,
      isFetchingStoreData: false,
      noResultsFound: false,
      isLoadingProducts: false,
      isLoadingMoreProducts: false,
      isRateStoreModalOpen: false,
      editedRating: null,
      isLoadingExclusiveOffers: false,
      isStoreCommentsModalOpen: false,
      isLoadingNews: false,
      isLoadingMoreNews: false,
      isLoadingComments: false,
      maxCommentItemsCountToFetch: 1,
      maxNewsItemsCountToFetch: 1,
      allNewsData: {
        currentPage: 1,
        hasReachedFullPages: false,
        list: []
      },
      // we inject all necessary data that needs to be save here in this object
      storeItem: {
        commentsFirstPage: [],
        newsFirstPage: [],
        exclusiveOffers: [],
        products: {
          selectedProductTypeId: "all",
          searchValue: "",
          selectedCategoryId:  "all",
          currentPage: 1,
          perPage: 20,
          pagesTotal: 1,
          maxPagesReached: false,
          list: []
        }
      },
      searchInput: "",
      isSearchBarFocused: false,
      isTogglingMuteNotification: false,
      lastSelectedProductTypeId: "",
      lastSearchInput: "",
      lastCategoryIdSelected: "",
      lastSelectedSortBy: "all",
      lastSelectedSubCategoryId: null,
      lastMinPrice: null,
      lastMaxPrice: null,
      basicFollowersCount: 0,
      followingInProgress: false,
      all: { id: "all", name_ar: "الكل", name_en: "All" },
      currentPage: 1,
      totalPages: 1,
      baseUrl: "",
      isShareProductModalOpen: false,
      selectedSubCategoryId: null,
      currentSubCategories: [],
      selectedSortBy: "all",
      isFilterModalOpen: false,
      tempFilters: {
        categoryId: null,
        subCategoryId: null,
        productTypeId: null,
        sortBy: null
      },
      minPrice: null,
      maxPrice: null,
      displayMinPrice: '',
      displayMaxPrice: '',
      safeProductListCache: null,
      _pendingProductRestore: null,
    };
  },
  components: {
    PagePortal,
    HomePopularStores,
    HomeTrendyProducts,
    Breadcrumb,
    ShareLink,
    StoreHeader,
    GoogleAdsBanner,
    StoreContactInfo,
    StoreRatingSection,
    ListSortingSelector,
    StoreExclusiveOffersSection,
    StoreNewsSection,
    ProductTypeItem,
    StoreNewsList,
    LoadingProductsList,
    ItemsSlider,
    CategoryItem,
  },
  computed: {
    ...mapState(useGlobalStore, ["savedStoresData"]),
    setPortalPage() {
      localStorage.setItem('storeItem', JSON.stringify(this.storeItem));
      return localStorage.setItem('portalPage', 'store');
    },
    allTypeOption() {
      return {value: 'all', text: this.$t('general.all')}
    },
    productTypeOptions() {
      return [
        {value: "retail", text: this.$t("panel.retail")},
        {value: "wholesale", text: this.$t("panel.wholesale")},
        {value: "offer", text: this.$t("panel.offer")},
      ];
    },
    storeId(){
      return this.storeItem?.id;
    },
    isNews(){
      return !!this.$route.query.news
    },
    isVerified() {
      return !!this.storeItem?.verified;
    },
    hasProductsReachedMaxPages(){
      return !!this.storeItem?.products?.maxPagesReached;
    },
    storeProducts(){
      // we include the products list here in the storesItem object to make it easier to be cached
      return this.storeItem?.products?.list || [];
    },
    finalizedDescriptionKey() {
      return this.descriptionKey || `description_${this.usedCookieLanguage}`;
    },
    finalizedNameKey() {
      return this.nameKey || `name_${this.usedCookieLanguage}`;
    },
    storeCategoriesList(){
      return this.storeItem?.categories || [];
    },
    shouldHideGoogleAdsBanner(){
      // Hide GoogleAdsBanner if category 383 is in the store's categories
      return this.storeCategoriesList.some(category => category.id === 383);
    },
    storeCategoriesAsKeywords(){
      return this.storeCategoriesList?.length > 0 ?
      this.storeCategoriesList
        .map((category) => category[this.finalizedNameKey])
        .join(', ') : []
    },
    disableProductFilterAndSearchInputs(){
      const { storeItem, storeProducts, isLoadingProducts } = this;
      const { searchValue, selectedCategoryId, selectedProductTypeId } = storeItem?.products || {};

      const areAllFiltersReset = !isLoadingProducts && !searchValue && (!selectedCategoryId || selectedCategoryId === 'all') && (!selectedProductTypeId || selectedProductTypeId === 'all');
      const isProductsListEmpty = storeProducts?.length < 1;
      return isProductsListEmpty && areAllFiltersReset;
    },
    storeBranchesList(){
      return this.storeItem?.branches || [];
    },
    relatedStoresList(){
      return this.storeItem?.related_stores || [];
    },
    commentsFirstPage(){
      return this.storeItem?.commentsFirstPage || [];
    },
    newsFirstPage(){
      return this.storeItem?.newsFirstPage?.slice(0, this.maxNewsItemsCountToFetch) || [];
    },
    allNewsItemsList(){
      const firstPagesArray = this.storeItem?.newsFirstPage || [];
      const resOfPages = this.allNewsData?.list || [];

      return [
        ...firstPagesArray,
        ...resOfPages
      ]
    },
    exclusiveOffersFirstPage(){
      return this.storeItem?.exclusiveOffersFirstPage || [];
    },
    isFollowing() {
      return !!this.storeItem?.following;
    },
    isMuted(){
      return this.storeItem?.muted_follow;
    },
    selectedCategoryId(){
      return this.storeItem?.products?.selectedCategoryId || "all";
    },
    selectedProductTypeId(){
      return this.storeItem?.products?.selectedProductTypeId || "all";
    },
    productTypeOptions(){
      const options = [{value: 'all', text: this.$t('general.all')}];
      
      const productTypes = {
        retail: {value: 'retail', text: this.$t('general.retail')},
        wholesale: {value: 'wholesale', text: this.$t('general.wholesale')}, 
        offer: {value: 'offer', text: this.$t('general.offer_noun')}
      };

      if (this.storeItem?.products_types) {
        Object.keys(productTypes).forEach(type => {
          if (this.storeItem.products_types.includes(type)) {
            options.push(productTypes[type]);
          }
        });
      }

      return options;
    },
    sortingOptions() {
      return [
        {value: "all", text: this.$t("general.list_sorting.all")},
        {value: "date", text: this.$t("general.list_sorting.date")},
        {value: "views", text: this.$t("general.list_sorting.views")},
        {value: "sales", text: this.$t("general.list_sorting.sales")},
        {value: "rating", text: this.$t("general.list_sorting.rating")},
        {value: "discount", text: this.$t("general.list_sorting.discount")},
      ]
    },
    filterSortOptions() {
      return [
        {value: "all", text: this.$t("general.all")},
        {value: "newest", text: this.$t("general.list_sorting.newest_to_oldest"), params: {sort_by: "date", sort_type: "desc"}},
        {value: "oldest", text: this.$t("general.list_sorting.oldest_to_newest"), params: {sort_by: "date", sort_type: "asc"}},
        {value: "price_high", text: this.$t("general.list_sorting.price_high_to_low"), params: {sort_by: "price", sort_type: "desc"}},
        {value: "price_low", text: this.$t("general.list_sorting.price_low_to_high"), params: {sort_by: "price", sort_type: "asc"}},
      ]
    },
    sortByOptions() {
      const options = [{value: 'all', text: this.$t('general.list_sorting.all')}];
      
      const sortTypes = {
        date: {value: 'date', text: this.$t('general.list_sorting.date'), params: {sort_by: "date", sort_type: "desc"}},
        views: {value: 'views', text: this.$t('general.list_sorting.views'), params: {sort_by: "views", sort_type: "desc"}},
        sales: {value: 'sales', text: this.$t('general.list_sorting.sales'), params: {sort_by: "sales", sort_type: "desc"}},
        rating: {value: 'rating', text: this.$t('general.list_sorting.rating'), params: {sort_by: "rating", sort_type: "desc"}},
        discount: {value: 'discount', text: this.$t('general.list_sorting.discount'), params: {sort_by: "discount", sort_type: "desc"}}
      };

      if (this.storeItem?.sort_list) {
        Object.keys(sortTypes).forEach(type => {
          if (this.storeItem.sort_list.includes(type)) {
            options.push(sortTypes[type]);
          }
        });
      }

      return options;
    },
    containerStyleValue() {
      const encodedUrl = this.storeItem?.background_image
        ? this.storeItem?.background_image.replaceAll(" ", "%20")
        : "";
      return {
        ...(this.storeItem.background_image
          ? {
              backgroundImage: `url('${encodedUrl}')`,
              backgroundRepeat: "no-repeat",
              backgroundAttachment: "fixed",
              backgroundPosition: "center",
              backgroundSize: "cover",
              filter: "blur(1.6px)",
              "-webkit-filter": "blur(1.6px)",
              transform: "scale(1.004)", //to prevent white borders around the image because of the blurring effect
              "z-index": -1
            }
          : {
              backgroundcolor: this.storeItem.background_color,
            }),
      };
    },
    storeCurrentURL() {
      return `${this.baseUrl}${buildStoreLink(this.storeItem?.username)}`;
    },
    breadcrumbLinks() {

      return [
            {
              text: this.$t("general.homepage"),
              path: ROUTESNAMES.user.homepage
            },
            {
              text: this.storeItem?.name || this.$t("general.store"),
              path: ""
            }
      ]
    },
    ratingData() {
      const { ratings_avg = 0, ratings_count = 0, name, username, verified, id } = this.storeItem || {};
      return {
        ratings_avg,
        ratings_count,
        store: {
          id,
          name,
          username,
          verified
        }
      };
    },
    formedPageTitle() {
      // Use meta_title from SEO fields if available, otherwise fallback to store name
      const metaTitleKey = `meta_title_${this.usedCookieLanguage}`;
      const metaTitle = this.storeItem?.[metaTitleKey];
      
      if (metaTitle) {
        return `${metaTitle} | ${this.$t("general.cezma")}`;
      }
      
      // Fallback to store name
      const title = this.storeItem?.name || this.storeUsername || this.$t("general.store");
      return `${title} | ${this.$t("general.cezma")}`;
    },
    formedDescription() {
      // Use meta_description from SEO fields if available, otherwise fallback to store description
      const metaDescriptionKey = `meta_description_${this.usedCookieLanguage}`;
      const metaDescription = this.storeItem?.[metaDescriptionKey];
      
      if (metaDescription) {
        return metaDescription;
      }
      
      // Fallback to store description
      return (
        this.storeItem?.[this.finalizedDescriptionKey] ||
        this.storeItem?.description_ar ||
        this.storeItem?.description_en ||
        this.$t("general.store_description") ||
        "Store on Cezma platform"
      );
    },
    formedKeywords() {
      // Use meta_keywords from SEO fields if available
      const metaKeywords = this.storeItem?.meta_keywords;
      
      if (metaKeywords) {
        // Convert comma-separated string to array
        return metaKeywords.split(',').map(k => k.trim()).filter(k => k);
      }
      
      // Fallback to storeSEOKeywords
      return this.storeSEOKeywords;
    },
    storeLogoAltText() {
      // Use image_alt_text from SEO fields if available
      const imageAltText = this.storeItem?.image_alt_text;
      
      if (imageAltText) {
        return imageAltText;
      }
      
      // Fallback to store name
      return this.storeItem?.name || this.storeUsername || this.$t("general.store");
    },
    storeDescription(){
      return this.storeItem[this.finalizedDescriptionKey];
    },
    storeLogo(){
      // Use storeItem data directly since it's available from SSR
      const logo = this.storeItem?.logo;
      console.log('storeLogo computed - storeItem logo:', this.storeItem?.logo, 'final logo:', logo);
      return logo;
    },
    URLLink() {
      if (process.client) {
        return typeof window !== "undefined" ? window.location.href : "";
      } else {
        return "";
      }
    },
    hasActiveFilters() {
      return this.selectedCategoryId !== 'all' || 
             this.selectedSubCategoryId !== null || 
             this.selectedProductTypeId !== 'all' ||
             this.hasPriceFilter ||
             (this.searchInput && this.searchInput.trim() !== '');
    },
    hasPriceFilter() {
      return (this.minPrice !== null && this.minPrice !== '') || 
             (this.maxPrice !== null && this.maxPrice !== '');
    },
    storeSEOKeywords() {
      // Generate SEO keywords from store data
      const keywords = [];
      
      // Add store name
      const storeName = this.storeItem?.name;
      if (storeName) {
        keywords.push(storeName);
      } else if (this.storeUsername) {
        keywords.push(this.storeUsername);
      }
      
      // Add store categories
      if (this.storeCategoriesList?.length > 0) {
        const categoryNames = this.storeCategoriesList.map(cat => cat[this.finalizedNameKey]);
        keywords.push(...categoryNames);
      }
      
      // Add store description keywords (first few words)
      const description = this.storeItem?.[this.finalizedDescriptionKey] || this.storeItem?.description_ar || this.storeItem?.description_en;
      if (description) {
        const descWords = description.split(' ').slice(0, 5);
        keywords.push(...descWords);
      }
      
      // Add general store-related keywords
      keywords.push(
        this.$t('general.store'),
        this.$t('general.shopping'),
        'Cezma',
        'online store'
      );
      
      // Remove duplicates and join
      return [...new Set(keywords)].filter(Boolean).join(', ');
    },
    shouldDisableIndexing() {      
      // Disable indexing if store ID is invalid
      if (!this.storeItem.id) {
        return true;
      }
      
      return false;
    },
  },
  watch:{
    storeItem:{
      deep: true,
      handler(val){
        // Only update Pinia store, don't call setResponse to avoid recursion
        const storeUsername = this.storeUsername;
        if (storeUsername && val) {
          this.updateStoresData({
            [storeUsername]: {
              ...val,
            },
          });
        }
      }
    },
    // Watch for store data changes to update meta tags
    'storeItem.name': {
      handler(newName) {
        if (newName) {
          // Force update of meta tags when store name becomes available
          this.$nextTick(() => {
            // The PagePortal component will automatically update meta tags
            // when its props change due to reactivity
          });
        }
      }
    },
    'storeItem.logo': {
      handler(newLogo) {
        console.log('Logo changed:', newLogo);
        if (newLogo) {
          this.$nextTick(() => {
            // The PagePortal component will automatically update meta tags
            // when its props change due to reactivity
          });
        }
      }
    },
    'storeItem.instagram': {
      handler(newInstagram) {
        if (newInstagram) {
          this.$nextTick(() => {
            // The PagePortal component will automatically update meta tags
            // when its props change due to reactivity
          });
        }
      }
    },
    pageLoading: {
      handler(isLoading) {
        if (!isLoading && this.storeItem?.name) {
          // Update meta tags when page finishes loading and store data is available
          this.$nextTick(() => {
            // The PagePortal component will automatically update meta tags
            // when its props change due to reactivity
          });
        }
      }
    },
  },
  methods: {
    arabizeNumbers,
    handleLikeClick(item){
      this.isLoadingComments = true;
      const formData = new FormData();
      formData.append('rating_id', item.item.id);

      this.makeRequest({
        owner: MAINROLES.user,
        url: `${item.type === 'like' ? APIREQUESTS.user.likeStoreComment.url : APIREQUESTS.user.dislikeStoreComment.url}`,
        method: item.type === 'like' ? APIREQUESTS.user.likeStoreComment.method : APIREQUESTS.user.dislikeStoreComment.method,
        formData: formData
      }).then(res => {
        if(res?.status){
          this.getComments();
        }else{
          this.notify({ type: 'error', msg: this.$t('general.error_occurred') });
        }
      }).catch(err => {
        this.notify({ type: 'error', msg: this.$t('general.error_occurred') });
      }).finally(() => {
        this.isLoadingComments = false;
      });
    },
    changeFollowersCountBasedOnFollowingState({allowForCounterChange, isFollowed, resetFollowersCounter}){
      // update the following state
      this.storeItem.following = isFollowed;
      // update the followers counter
      if(resetFollowersCounter){
        this.storeItem.followers_count = this.basicFollowersCount;
      }else if(allowForCounterChange){
        if(isFollowed){
          this.storeItem.followers_count +=1;
          this.storeItem.muted_follow = 0;
        }else{
          if(this.storeItem.followers_count > 0){
            this.storeItem.followers_count -=1;
          }
        }
      }
    },       
    setTheFollowingState ({newState, allowForCounterChange = false, resetFollowersCounter = false}) {
      // it toggles from the backend without sending the new state to toggle with      
      this.changeFollowersCountBasedOnFollowingState({allowForCounterChange, isFollowed: !!newState, resetFollowersCounter});
    },
    toggleFollowingState() {
            const { id } = this.storeItem || {};
            const isOffline = !navigator.onLine;
            if (this.followingInProgress || !id || isOffline) {
                return;
            }
            this.followingInProgress = true;
           
            // if(this.isUserLoggedIn){
            //   this.setTheFollowingState({newState: !this.isFollowing, allowForCounterChange: true});
            // }

            const bodyRequest = {
                store_id: id
            };
            
            this.makeRequest({
                owner: MAINROLES.user,
                url: APIREQUESTS.user.followStore.url,
                method: APIREQUESTS.user.followStore.method,
                formData: JSON.stringify(bodyRequest)
            })
                .then(res => {
                    if (!res?.status) {
                        this.setTheFollowingState({newState: this.isFollowing, resetFollowersCounter: true});
                        // fail
                        this.notify({
                            type: "error",
                            msg: res?.message || this.$t("general.failed")
                        });
                    } else {
                        // success
                        this.setTheFollowingState({newState: +res.data?.following});
                    }
                })
                .catch(err => {
                  // fail
                    this.setTheFollowingState({newState: this.isFollowing, resetFollowersCounter: true });
                    // console.error(err);
                    this.notify({
                        type: "error",
                        msg:
                            (typeof err === "string" ? err : err?.data?.message) ||
                            this.$t("general.failed")
                    });
                })
                .finally(() => {
                    this.followingInProgress = false;
                });
        },
    // all the get store-data requests pours into here
    setResponse({ response = {} }) {
      const storeUsername = this.storeUsername;
      
      if (storeUsername && response) {
        // cache the response and the previous search value & selected category and update them in the global storage (pinia)
        this.updateStoresData({
          [storeUsername]: {
            ...response,
          },
        });
      }
      
      // Set the store data to the component's storeItem
      this.storeItem = response;
      
      // update component state
      this.noResultsFound = !!(this.storeProducts?.length <=0);
      const { searchValue } = response?.products || {};
      this.searchInput = searchValue;
      
      // Set basic followers count
      this.basicFollowersCount = response?.followers_count;
      
      // Set tracking parameters
      const { list, searchValue: responseSearchValue = "", selectedCategoryId = "all", selectedProductTypeId = "all" } = response?.products || {};
      this.lastSearchInput = responseSearchValue;
      this.lastCategoryIdSelected = selectedCategoryId;
      this.lastSelectedProductTypeId = selectedProductTypeId;
    },
    changeShareProductModalState(val) {
      this.isShareProductModalOpen = val;
    },
    changeRateStoreModalState(val) {
      this.isRateStoreModalOpen = val;
      // Clear editedRating when modal is closed
      if (!val) {
        this.editedRating = null;
      }
    },
    onEditRatingOpenModal({ item }) {
      if (!item || !item.id) {
        return;
      }
      // Set the rating to edit and open the modal
      this.editedRating = item;
      this.isRateStoreModalOpen = true;
    },
    changeStoreCommentsModalState(val){
      this.isStoreCommentsModalOpen = val;
    },
    onStoreImageError(e) {
      e.target.src = this.defaultStoreImg;
      e.onerror = null;
      return true;
    },   
    onProductsSearchChange(){
      if (!this.searchInput) {
        this.handleSearch(false);
      }
    },
    handleLoadingMoreProducts() {
      if (this.reachedEnd) return;
      this.getProducts({isLoadMore: true});
    },
    onProductTypeFilterChange(newValue){
      if (this.isFetchingStoreData) return;
      this.getProducts({selectedProductTypeId: newValue});
    },
    onCategoryFilterChange({sortBy}) {
      if (this.isFetchingStoreData) return;
      this.selectedSubCategoryId = null; // Reset subcategory selection
      this.updateSubCategories(sortBy); // Get subcategories for selected category
      this.getProducts({selectedCategoryId: sortBy});
    },
    handleSearch() {
      if (this.isFetchingStoreData) return;
      const searchInput = this.searchInput.trim();
      this.getProducts({searchValue: searchInput});
    },
    openStoreInteractionModal(){
      this.isShareProductModalOpen = true;
    },
    directUserToLoginPage(){
        this.$router?.push(ROUTESNAMES.user.login);
    },
    handleViewingStoreInteractionModal(modalType){
      if (modalType === 'comments') {
        this.changeStoreCommentsModalState(true);
      } else {
        if (!this.isUserLoggedIn) {
          this.directUserToLoginPage();
        } else {
          this.changeRateStoreModalState(true);
        }
      }
    },
    fetchStoreData() {
      this.isFetchingStoreData = true;

      this.makeRequest({
        ...(this.isUserLoggedIn && {owner: MAINROLES.user}),
        url: `${APIREQUESTS.user.getStore.url}/${this.storeUsername}`,
        method: APIREQUESTS.user.getStore.method,
        includeUID: true
      })
        .then((res) => {
          if (!res?.status) {
            this.storeItem = null;
            return;
          }
          if(res.data?.products?.length <= 0){
            this.noResultsFound = true;
          }
          const responseData = res?.data || {};
          const singleProductCopy = this.storeItem || {};

          const newResponseObject = {
            ...singleProductCopy,
            ...responseData,
          }
          this.setResponse({ response: newResponseObject });
        })
        .catch((err) => {
          this.storeItem = null;
        })
        .finally(() => {
          this.isFetchingStoreData = false;
        });
    },
    getComments() {
       return new Promise((resolve, reject) => {
        this.isLoadingComments = true;
        const queryString = constructQueryString([
                      { key: "store_id", value: this.storeId},
                      { key: 'page', value: 1 },
                  ]);
        this.makeRequest({
          ...(this.isUserLoggedIn && {owner: MAINROLES.user}),
          url: `${APIREQUESTS.user.getStoreRatings.url}${queryString}`,
          method: APIREQUESTS.user.getStoreRatings.method,
        })
          .then((res) => {
            resolve();
            const responseData = res?.data || [];
            const singleProductCopy = {...this.storeItem || {}};

            const newResponseObject = {
              ...singleProductCopy,
              commentsFirstPage: responseData?.slice(0, this.maxCommentItemsCountToFetch)
            }
            this.setResponse({ response: newResponseObject });
          }).catch(() => {
            reject();
          }).finally(() => {
            this.isLoadingComments = false;
          });
       }); 
    },
    getNews({ isLoadMore }) {
       return new Promise((resolve, reject) => {

        if(!isLoadMore){
          this.isLoadingNews = true;
        }else{
          this.isLoadingMoreNews = true;
        }
        
        const queryString = constructQueryString([
                      { key: "store_id", value: this.storeId},
                      { key: 'page', value: isLoadMore ? this.allNewsData.currentPage + 1 : 1 },
                  ]);

        this.makeRequest({
          ...(this.isUserLoggedIn && {owner: MAINROLES.user}),
          url: `${APIREQUESTS.user.getStoreNews.url}${queryString}`,
          method: APIREQUESTS.user.getStoreNews.method,
        })
          .then((res) => {
            resolve();
            const newItems = res?.data || [];
            // once loaded, get the first page
            if(!isLoadMore){
              const singleProductCopy = {...this.storeItem || {}};
              const newResponseObject = {
                ...singleProductCopy,
                newsFirstPage: newItems
              }
              this.setResponse({ response: newResponseObject });
            }else{
              // in case of load more
              const oldItems = this.allNewsData.list || [];

              this.allNewsData = {
                ...this.allNewsData, 
                hasReachedFullPages: checkPaginationEnd(res),
                currentPage: +res?.current_page,
                list: [
                    ...oldItems,
                    ...newItems
                ]
              }
            }
          }).catch(() => {
            reject();
          }).finally(() => {
            this.isLoadingNews = false;
            this.isLoadingMoreNews = false;
          });
       }); 
    },
    toggleBellNotification(){
      if(this.isTogglingMuteNotification) return;
      const bodyRequest = {
        store_id: this.storeId
      } 
      this.isTogglingMuteNotification = true;
      this.makeRequest({
        owner: MAINROLES.user,
        url: APIREQUESTS.user.toggleStoreMutingState.url,
        method: APIREQUESTS.user.toggleStoreMutingState.method,
        formData: JSON.stringify(bodyRequest)
      })
        .then(res => {
          this.isTogglingMuteNotification = false;
          if (!res?.status) {
            // fail
            this.notify({
              type: "error",
              msg: res?.message || this.$t("general.failed")
            });
          } else {
            // success
            let tempStoreData = this.storeItem;
            if (tempStoreData) {
              tempStoreData.muted_follow = +res.data?.muted;
            }
            this.setResponse({ response: tempStoreData });
          }
        })
        .catch(err => {
          this.isTogglingMuteNotification = false;
          // console.error(err);
          this.notify({
            type: "error",
            msg:
              (typeof err === "string" ? err : err?.data?.message) ||
              this.$t("general.failed")
          });
        })
    },
    getExclusiveOffers() {
       return new Promise((resolve, reject) => {
        this.isLoadingExclusiveOffers = true;
        const queryString = constructQueryString([
                      { key: "store_id", value: this.storeId},
                      { key: 'page', value: 1 },
                  ]);
        this.makeRequest({
          ...(this.isUserLoggedIn && {owner: MAINROLES.user}),
          url: `${APIREQUESTS.user.getExclusiveOffers.url}${queryString}`,
          method: APIREQUESTS.user.getExclusiveOffers.method,
        })
          .then((res) => {
            resolve();
            const responseData = res?.data || [];
            const singleProductCopy = {...this.storeItem || {}};

            const newResponseObject = {
              ...singleProductCopy,
              exclusiveOffersFirstPage: responseData
            }
            this.setResponse({ response: newResponseObject });
          }).catch(() => {
            reject();
          }).finally(() => {
            this.isLoadingExclusiveOffers = false;
          });
       }); 
    },
    loadMoreNewsData(){
      this.getNews({isLoadMore: true});
    },
    getProducts({isLoadMore, searchValue = this.searchInput, selectedCategoryId = this.selectedCategoryId, selectedProductTypeId = this.selectedProductTypeId, selectedSubCategoryId = this.selectedSubCategoryId, sortBy = this.selectedSortBy, minPrice = this.minPrice, maxPrice = this.maxPrice}) {
      return new Promise((resolve, reject) => {
        // getProducts should always get called on the client-side to prevent affecting the page's load time
        const storeId = this.storeId;
        if(!process.client || !storeId){
          reject();
          return;
        }
        
        // resets the 'noResultsFound' state
        this.noResultsFound = false;
        
        // Update tracking parameters
        this.lastSearchInput = searchValue;
        this.lastCategoryIdSelected = selectedCategoryId;
        this.lastSelectedProductTypeId = selectedProductTypeId;
        this.lastSelectedSortBy = sortBy;
        this.lastSelectedSubCategoryId = selectedSubCategoryId;
        this.lastMinPrice = minPrice;
        this.lastMaxPrice = maxPrice;
        
        if(searchValue && (searchValue != '')){
          // Update tracking parameters
          this.lastSearchInput = searchValue;
          this.lastCategoryIdSelected = selectedCategoryId;
          this.lastSelectedProductTypeId = selectedProductTypeId;
          this.lastSelectedSortBy = sortBy;
          this.lastSelectedSubCategoryId = selectedSubCategoryId;
          this.lastMinPrice = minPrice;
          this.lastMaxPrice = maxPrice;
        }
        
        // Clear the product list when applying new filters (not when loading more)
        if (!isLoadMore) {
          this.storeItem = {
            ...this.storeItem,
            products: {
              ...this.storeItem.products,
              searchValue: searchValue || "",
              selectedProductTypeId: selectedProductTypeId || 'all',
              selectedCategoryId: selectedCategoryId || "",
              list: [], // Clear the product list when applying new filters
              currentPage: 1 // Reset current page when applying new filters
            }
          }
        } else {
          // Just update the filter parameters without clearing the list when loading more
          this.storeItem = {
            ...this.storeItem,
            products: {
              ...this.storeItem.products,
              searchValue: searchValue || "",
              selectedProductTypeId: selectedProductTypeId || 'all',
              selectedCategoryId: selectedCategoryId || ""
            }
          }
        }

        if(!isLoadMore){
          this.isLoadingProducts = true;
        }else{
          this.isLoadingMoreProducts = true;
        }
        console.log('sortBy from getProducts: ',sortBy)
        // Get sort parameters if a specific sort option is selected
        let sortParams = {};
        if(sortBy && sortBy !== 'all') {
          const sortOption = this.filterSortOptions.find(opt => opt.value === sortBy);
          const sortTapsOption = this.sortByOptions.find(opt => opt.value === sortBy);
          if(sortOption && sortOption.params) {
            sortParams = sortOption.params;
          }else if(sortTapsOption && sortTapsOption.params){
            sortParams = sortTapsOption.params;
          }
        }
        
        // Get the current page from the storeItem.products object
        const currentPageToUse = this.storeItem?.products?.currentPage || 1;
        
        let queryObj = {
          store_id: storeId,
          with_ads: 0,
          ...(searchValue && { search: encodeURIComponent(searchValue) }),
          ...((selectedProductTypeId && selectedProductTypeId !== 'all') && { product_type: selectedProductTypeId }),
          ...((selectedCategoryId && selectedCategoryId !== 'all') && { category_id: selectedCategoryId }),
          ...(selectedSubCategoryId && { subcategory_id: selectedSubCategoryId }),
          ...(sortParams.sort_by && { sort_by: sortParams.sort_by }),
          ...(sortParams.sort_type && { sort_type: sortParams.sort_type }),
          ...(isLoadMore && { page: currentPageToUse + 1 }), // Use the current page from storeItem.products
          // Add price filter parameters
          ...(minPrice !== null && minPrice !== '' && { min_price: minPrice }),
          ...(maxPrice !== null && maxPrice !== '' && { max_price: maxPrice }),
        };
        let queryStr = Object.entries(queryObj)
          .map((item, i) => (i == 0 ? item.join("=") : `&${item.join("=")}`))
          .join("");

        this.makeRequest({
          ...(this.isUserLoggedIn && {owner: MAINROLES.user}),
          url: `${APIREQUESTS.user.getProducts.url}?${queryStr}`,
          method: APIREQUESTS.user.getProducts.method,
          includeUID: true
        })
          .then((res) => {
            resolve();
            if (!res?.status) {
              this.storeItem.products = null;
            }
            if(res.data?.products?.length <= 0){
              this.noResultsFound = true;
            }
            const { data = [], current_page = 1, pages_total = 1, per_page = 20 } = res || {};
            const newProductsArray = [
                ...(isLoadMore ? [...this.storeProducts, ...data]: data)
            ]
            const newResponseObject = {
              ...this.storeItem,
              products: {
                ...this.storeItem.products,
                currentPage: current_page, // Update the current page from the response
                perPage: per_page,
                pagesTotal: pages_total,
                maxPagesReached: checkPaginationEnd(res),
                list: newProductsArray
              }
            }
            this.setResponse({ response: newResponseObject });
            if(res.data?.products?.length == 0  ){
              this.reachedEnd = true;
            }
            
            // After pagination completes, check if we need to continue loading or restore position
            if (this._pendingProductRestore) {
              const { productId, page } = this._pendingProductRestore;
              
              this.$nextTick(() => {
                setTimeout(() => {
                  // Check if we've reached the target page
                  if (this.storeItem?.products?.currentPage >= page) {
                    console.log(`✅ Reached target page ${page}, restoring product position...`);
                    this.restoreProductPositionAfterPagination(productId, page);
                    this._pendingProductRestore = null; // Clear the flag
                  } else {
                    // Continue loading pages until we reach the target
                    console.log(`⏳ Still loading pages... current: ${this.storeItem?.products?.currentPage}, target: ${page}`);
                    this.loadPagesUntilTarget(page, productId);
                  }
                }, 400); // Wait for DOM to update with new products
              });
            }
          })
          .catch((err) => {
            reject();
            this.storeItem.products = null;
          })
          .finally(() => {
            this.isLoadingProducts = false;
            this.isLoadingMoreProducts = false;
          });
      });
    },
    triggerContactPixelEvent(){
      fbq('track', 'Contact');
    },
    getAll({searchValue, selectedCategoryId}){
      if(!process.client) return;
      this.getProducts({searchValue, selectedCategoryId}).finally(() =>{
         this.getComments();
         this.getExclusiveOffers();
         this.getNews({});
      })
    },
    onSubCategoryFilterChange(subCategory) {
      if (this.isFetchingStoreData) return;
      this.selectedSubCategoryId = subCategory.id;
      this.getProducts({
        selectedCategoryId: this.selectedCategoryId,
        selectedSubCategoryId: subCategory.id
      });
    },
    updateSubCategories(categoryId) {
      if (categoryId === 'all') {
        this.currentSubCategories = [];
        return;
      }
      
      // Find the selected category and get its subcategories
      const selectedCategory = this.sortingOptions.find(cat => cat.value === categoryId);
      this.currentSubCategories = selectedCategory?.subcategories || [];
    },
    onSortChange({sortBy}) {
      if (this.isFetchingStoreData) return;
      this.selectedSortBy = sortBy;
      console.log('sortBy',sortBy)
      this.getProducts({
        selectedCategoryId: this.selectedCategoryId,
        selectedProductTypeId: this.selectedProductTypeId,
        sortBy: sortBy
      });
    },
    switchToProductsTab() {
      this.activeTab = 'products';
      // Reset category filter
      if (this.selectedCategoryId !== 'all') {
        this.getProducts({
          selectedCategoryId: 'all',
          selectedSubCategoryId: null,
          sortBy: this.selectedSortBy
        });
      }
    },
    switchToCategoriesTab() {
      this.activeTab = 'categories';
      // Reset sort filter
      if (this.selectedSortBy !== 'all') {
        this.selectedSortBy = 'all';
        this.getProducts({
          selectedCategoryId: this.selectedCategoryId,
          selectedSubCategoryId: this.selectedSubCategoryId,
          sortBy: 'all'
        });
      }
    },
    resetFilters() {
      this.searchInput = "";
      this.selectedSortBy = "all";
      this.selectedCategoryId = "all";
      this.selectedProductTypeId = "all";
      this.selectedSubCategoryId = null;
      this.minPrice = null;
      this.maxPrice = null;
      this.currentSubCategories = [];
      
      // Reset tracking properties
      this.lastSearchInput = "";
      this.lastCategoryIdSelected = "all";
      this.lastSelectedProductTypeId = "all";
      this.lastSelectedSortBy = "all";
      this.lastSelectedSubCategoryId = null;
      this.lastMinPrice = null;
      this.lastMaxPrice = null;
      
      // Reset and fetch products with default filters
      this.getProducts({
        searchValue: "",
        selectedCategoryId: "all",
        selectedProductTypeId: "all",
        selectedSubCategoryId: null,
        sortBy: "all",
        minPrice: null,
        maxPrice: null
      });
    },
    openFilterModal() {
      // Save current filter state to temp filters
      this.tempFilters = {
        categoryId: this.selectedCategoryId,
        subCategoryId: this.selectedSubCategoryId,
        productTypeId: this.selectedProductTypeId,
        sortBy: this.selectedSortBy,
        minPrice: this.minPrice,
        maxPrice: this.maxPrice
      };
      this.isFilterModalOpen = true;
      
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    },
    closeFilterModal() {
      this.isFilterModalOpen = false;
      
      // Restore body scrolling
      document.body.style.overflow = '';
    },
    applyFiltersAndCloseModal() {
      // Filters are already applied as they're changed
      this.closeFilterModal();
    },
    resetFiltersAndCloseModal() {
      this.resetFilters();
      this.closeFilterModal();
    },
    getCategoryName(categoryId) {
      if (categoryId === 'all') return this.$t('general.all');
      const category = this.storeCategoriesList.find(cat => cat.id === categoryId);
      return category ? category[this.finalizedNameKey] : categoryId;
    },
    
    getSubCategoryName(subCategoryId) {
      const subCategory = this.currentSubCategories.find(sub => sub.id === subCategoryId);
      return subCategory ? subCategory[this.finalizedNameKey] : subCategoryId;
    },
    
    getProductTypeName(typeId) {
      const type = this.productTypeOptions.find(opt => opt.value === typeId);
      return type ? type.text : typeId;
    },
    
    getSortByName(sortId) {
      // First check in the filter sort options
      const filterSortOption = this.filterSortOptions.find(opt => opt.value === sortId);
      if (filterSortOption) return filterSortOption.text;
      
      // Then check in the regular sort options
      const sortOption = this.sortByOptions.find(opt => opt.value === sortId);
      return sortOption ? sortOption.text : sortId;
    },
    getPriceFilterLabel() {
      if (this.minPrice !== null && this.minPrice !== '' && this.maxPrice !== null && this.maxPrice !== '') {
        return `${this.$t('general.price')}: ${this.formatNumberWithCommas(this.minPrice)} - ${this.formatNumberWithCommas(this.maxPrice)}`;
      } else if (this.minPrice !== null && this.minPrice !== '') {
        return `${this.$t('general.price')} ≥ ${this.formatNumberWithCommas(this.minPrice)} ج.م`;
      } else if (this.maxPrice !== null && this.maxPrice !== '') {
        return `${this.$t('general.price')} ≤ ${this.formatNumberWithCommas(this.maxPrice)} ج.م`;
      }
      return '';
    },
    
    formatNumberWithCommas(number) {
      if (number === null || number === '') return '';
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    
    formatPriceInputs() {
      // Remove non-numeric characters except commas for display
      if (this.displayMinPrice) {
        // Store the raw number value
        this.minPrice = this.displayMinPrice.replace(/,/g, '');
        // Format with commas for display
        this.displayMinPrice = this.formatNumberWithCommas(this.minPrice);
      } else {
        this.minPrice = null;
      }
      
      if (this.displayMaxPrice) {
        // Store the raw number value
        this.maxPrice = this.displayMaxPrice.replace(/,/g, '');
        // Format with commas for display
        this.displayMaxPrice = this.formatNumberWithCommas(this.maxPrice);
      } else {
        this.maxPrice = null;
      }
      
      // Validate that max price is greater than min price if both are set
      if (this.minPrice !== null && this.maxPrice !== null && 
          Number(this.maxPrice) < Number(this.minPrice)) {
        this.maxPrice = this.minPrice;
        this.displayMaxPrice = this.displayMinPrice;
      }
    },
    
    applyPriceFilter() {
      // Don't apply if we're already fetching data
      if (this.isFetchingStoreData) return;
      
      // Format inputs first
      this.formatPriceInputs();
      
      // Apply the price filter with the numeric values
      this.getProducts({
        minPrice: this.minPrice,
        maxPrice: this.maxPrice
      });
    },
    
    clearPriceFilter() {
      this.minPrice = null;
      this.maxPrice = null;
      this.displayMinPrice = '';
      this.displayMaxPrice = '';
      this.getProducts({
        minPrice: null,
        maxPrice: null
      });
    },
    
    // Get current filter state for caching purposes
    getCurrentFilters() {
      return {
        search: this.searchInput || "",
        category: this.selectedCategoryId !== 'all' ? this.selectedCategoryId : "",
        subcategory: this.selectedSubCategoryId || "",
        productType: this.selectedProductTypeId !== 'all' ? this.selectedProductTypeId : "",
        sort: this.selectedSortBy !== 'all' ? this.selectedSortBy : "",
        minPrice: this.minPrice || "",
        maxPrice: this.maxPrice || "",
      };
    },
    
    // Save product card position when clicked for precise restoration
    saveProductCardPosition(productId) {
      if (!safeProductListCache || !process.client) return;

      try {
        // CRITICAL: Capture scroll position IMMEDIATELY before any navigation or DOM changes
        const currentScrollX = window.scrollX || window.pageXOffset || 0;
        const currentScrollY = window.scrollY || window.pageYOffset || 0;

        // Get current cache key and page
        const currentFilters = this.getCurrentFilters();
        const cacheKey = safeProductListCache.generateCacheKey(
          currentFilters,
          this.selectedSortBy || "all",
          this.selectedProductTypeId !== 'all' ? { value: this.selectedProductTypeId } : null
        );
        const currentPage = this.storeItem?.products?.currentPage || 1;

        // Get the product card element
        const productCard = document.querySelector(
          `[data-product-id="${productId}"]`
        );
        if (!productCard) {
          console.log(`❌ Product card element not found for ID: ${productId}`);
          return;
        }

        // Get the product card position relative to the viewport
        const cardRect = productCard.getBoundingClientRect();
        const absoluteY = cardRect.top + currentScrollY;
        const absoluteX = cardRect.left + currentScrollX;
        
        // Validate that we have a valid position
        if (currentScrollY === 0 && cardRect.top < -100) {
          console.warn(`⚠️ Suspicious position detected for product ${productId}: scrollY=0 but card top=${cardRect.top}. Skipping save.`);
          return;
        }
        
        const cardPosition = {
          x: absoluteX,
          y: absoluteY,
          pageScrollX: currentScrollX,
          pageScrollY: currentScrollY,
          cardHeight: cardRect.height,
          cardWidth: cardRect.width,
        };

        // Save the product card position
        const saved = safeProductListCache.saveProductCardPosition(
          cacheKey,
          currentPage,
          productId,
          cardPosition
        );

        if (saved) {
          console.log(
            `📍 Product card position saved for ${productId}:`,
            cardPosition
          );
          
          // Store product ID and context for restoration when returning from product page
          this.storeProductNavigationContext(productId, cacheKey, currentPage);
        }
      } catch (error) {
        console.error("Failed to save product card position:", error);
      }
    },

    // Save exclusive offer position when clicked for precise restoration
    saveExclusiveOfferPosition(productId) {
      if (!safeProductListCache || !process.client || !productId) return;

      try {
        // CRITICAL: Capture scroll position IMMEDIATELY before any navigation or DOM changes
        const currentScrollX = window.scrollX || window.pageXOffset || 0;
        const currentScrollY = window.scrollY || window.pageYOffset || 0;

        // For exclusive offers, use a special cache key
        // They don't have filters/pagination like regular products
        const cacheKey = `exclusive_offers_${this.storeId}`;
        const currentPage = 1; // Exclusive offers are always on page 1

        // Get the exclusive offer card element
        const offerCard = document.querySelector(
          `[data-product-id="${productId}"]`
        );
        if (!offerCard) {
          console.log(`❌ Exclusive offer card element not found for ID: ${productId}`);
          return;
        }

        // Get the card position relative to the viewport
        const cardRect = offerCard.getBoundingClientRect();
        const absoluteY = cardRect.top + currentScrollY;
        const absoluteX = cardRect.left + currentScrollX;
        
        // Validate that we have a valid position
        if (currentScrollY === 0 && cardRect.top < -100) {
          console.warn(`⚠️ Suspicious position detected for exclusive offer ${productId}: scrollY=0 but card top=${cardRect.top}. Skipping save.`);
          return;
        }
        
        const cardPosition = {
          x: absoluteX,
          y: absoluteY,
          pageScrollX: currentScrollX,
          pageScrollY: currentScrollY,
          cardHeight: cardRect.height,
          cardWidth: cardRect.width,
        };

        // Save the exclusive offer card position
        const saved = safeProductListCache.saveProductCardPosition(
          cacheKey,
          currentPage,
          productId,
          cardPosition
        );

        if (saved) {
          console.log(
            `📍 Exclusive offer position saved for ${productId}:`,
            cardPosition
          );
          
          // Store exclusive offer ID and context for restoration when returning from product page
          this.storeExclusiveOfferNavigationContext(productId, cacheKey, currentPage);
        }
      } catch (error) {
        console.error("Failed to save exclusive offer position:", error);
      }
    },

    // Store product navigation context for restoration
    storeProductNavigationContext(productId, cacheKey, page) {
      if (typeof window === 'undefined') return;
      
      try {
        const context = {
          productId,
          cacheKey,
          page,
          timestamp: Date.now(),
          portalPage: 'store'
        };
        sessionStorage.setItem('lastClickedProduct', JSON.stringify(context));
        console.log(`💾 Stored product navigation context: ${productId}`);
      } catch (error) {
        console.error('Failed to store product navigation context:', error);
      }
    },

    // Store exclusive offer navigation context for restoration
    storeExclusiveOfferNavigationContext(productId, cacheKey, page) {
      if (typeof window === 'undefined') return;
      
      try {
        const context = {
          productId,
          cacheKey,
          page,
          timestamp: Date.now(),
          portalPage: 'store',
          isExclusiveOffer: true
        };
        sessionStorage.setItem('lastClickedProduct', JSON.stringify(context));
        console.log(`💾 Stored exclusive offer navigation context: ${productId}`);
      } catch (error) {
        console.error('Failed to store exclusive offer navigation context:', error);
      }
    },

    // Get stored product navigation context
    getStoredProductNavigationContext() {
      if (typeof window === 'undefined') return null;
      
      try {
        const stored = sessionStorage.getItem('lastClickedProduct');
        if (!stored) return null;
        
        const context = JSON.parse(stored);
        // Check if context is still valid (not too old - 1 hour)
        const age = Date.now() - (context.timestamp || 0);
        const maxAge = 60 * 60 * 1000; // 1 hour
        if (age > maxAge) {
          sessionStorage.removeItem('lastClickedProduct');
          return null;
        }
        return context;
      } catch (error) {
        console.error('Failed to get stored product navigation context:', error);
        return null;
      }
    },

    // Clear stored product navigation context
    clearStoredProductNavigationContext() {
      if (typeof window === 'undefined') return;
      sessionStorage.removeItem('lastClickedProduct');
    },

    // Try to restore product position after page loads
    tryRestoreProductPosition() {
      if (typeof window === 'undefined' || !safeProductListCache) return;
      
      const context = this.getStoredProductNavigationContext();
      if (!context) return;
      
      // Check if we're on the correct portal page
      if (context.portalPage !== 'store') return;
      
      // Handle exclusive offers restoration
      if (context.isExclusiveOffer) {
        this.tryRestoreExclusiveOfferPosition(context);
        return;
      }
      
      // Verify cache key still matches (filters/sorting haven't changed)
      const currentFilters = this.getCurrentFilters();
      const currentCacheKey = safeProductListCache.generateCacheKey(
        currentFilters,
        this.selectedSortBy || "all",
        this.selectedProductTypeId !== 'all' ? { value: this.selectedProductTypeId } : null
      );
      
      if (context.cacheKey !== currentCacheKey) {
        console.log('🗑️ Cache key mismatch, clearing stored product context');
        this.clearStoredProductNavigationContext();
        return;
      }
      
      // Check if products are loaded
      if (!this.storeProducts || this.storeProducts.length === 0) {
        console.log('⏳ Products not loaded yet, will retry restoration');
        setTimeout(() => {
          this.tryRestoreProductPosition();
        }, 500);
        return;
      }
      
      const savedPage = context.page || 1;
      const currentPage = this.storeItem?.products?.currentPage || 1;
      
      // Strategy: Load the correct page first if needed, then restore position after pagination
      if (savedPage !== currentPage && savedPage > 1) {
        console.log(`📄 Product was on page ${savedPage}, current page is ${currentPage}. Loading page ${savedPage} first...`);
        
        // Set flag to restore position after pagination completes
        this._pendingProductRestore = {
          productId: context.productId,
          page: savedPage
        };
        
        // Load pages sequentially until we reach the target page
        this.loadPagesUntilTarget(savedPage, context.productId);
      } else {
        // Already on correct page, just restore position after products are loaded
        this.$nextTick(() => {
          setTimeout(() => {
            this.restoreProductPositionAfterPagination(context.productId, savedPage);
          }, 500);
        });
      }
    },

    // Load pages sequentially until we reach the target page
    loadPagesUntilTarget(targetPage, productId) {
      const currentPage = this.storeItem?.products?.currentPage || 1;
      
      if (currentPage >= targetPage) {
        // We've reached the target page, restoration will happen in API response handler
        console.log(`✅ Reached target page ${targetPage}`);
        return;
      }
      
      // Load next page
      console.log(`📄 Loading page ${currentPage + 1} to reach target page ${targetPage}...`);
      
      this.handleLoadingMoreProducts();
      
      // Continue loading if needed (will be handled in API response)
    },

    // Restore product position after pagination has completed
    restoreProductPositionAfterPagination(productId, page) {
      if (!safeProductListCache) return;
      
      console.log(`🎯 Restoring product position for ${productId} on page ${page} after pagination`);
      
      // Verify we're on the correct page
      const currentPage = this.storeItem?.products?.currentPage || 1;
      if (currentPage !== page) {
        console.log(`⚠️ Page mismatch: expected ${page}, got ${currentPage}. Retrying...`);
        // Retry after a bit more delay
        setTimeout(() => {
          this.restoreProductPositionAfterPagination(productId, page);
        }, 500);
        return;
      }
      
      // Check if product exists in DOM
      const productElement = document.querySelector(`[data-product-id="${productId}"]`);
      if (!productElement) {
        console.log(`❌ Product ${productId} not found in DOM. Products may still be loading.`);
        // Retry after delay
        setTimeout(() => {
          this.restoreProductPositionAfterPagination(productId, page);
        }, 500);
        return;
      }
      
      // Now restore the position
      const restored = this.restoreToProductCard(productId);
      
      if (restored) {
        console.log(`✅ Successfully restored to product ${productId}`);
        this.clearStoredProductNavigationContext();
        // Re-enable package restoration after product restoration completes
        setTimeout(() => {
          console.log('📦 Product restoration complete, re-enabling package restoration');
        }, 1000);
      } else {
        console.log(`❌ Failed to restore product position. Clearing context.`);
        this.clearStoredProductNavigationContext();
      }
    },

    // Restore scroll to a specific product card
    restoreToProductCard(productId) {
      if (!safeProductListCache) return false;

      try {
        // Get current cache key and page
        const currentFilters = this.getCurrentFilters();
        const cacheKey = safeProductListCache.generateCacheKey(
          currentFilters,
          this.selectedSortBy || "all",
          this.selectedProductTypeId !== 'all' ? { value: this.selectedProductTypeId } : null
        );
        const currentPage = this.storeItem?.products?.currentPage || 1;

        // Restore to the specific product card
        const restored = safeProductListCache.restoreToProductCard(
          cacheKey,
          currentPage,
          productId
        );

        if (restored) {
          console.log(`🎯 Scroll restored to product card: ${productId}`);
        } else {
          console.log(
            `❌ Failed to restore scroll to product card: ${productId}`
          );
        }

        return restored;
      } catch (error) {
        console.error("Failed to restore to product card:", error);
        return false;
      }
    },
    
    // Try to restore exclusive offer position
    tryRestoreExclusiveOfferPosition(context) {
      if (typeof window === 'undefined' || !safeProductListCache || !context) return;
      
      const { productId, cacheKey } = context || {};
      
      if (!productId || !cacheKey) {
        console.log('❌ Invalid exclusive offer context, missing productId or cacheKey');
        this.clearStoredProductNavigationContext();
        return;
      }
      
      // Verify cache key matches
      const expectedCacheKey = `exclusive_offers_${this.storeId}`;
      if (cacheKey !== expectedCacheKey) {
        console.log('🗑️ Exclusive offer cache key mismatch, clearing context');
        this.clearStoredProductNavigationContext();
        return;
      }
      
      // Check if exclusive offers are loaded
      if (!this.exclusiveOffersFirstPage || this.exclusiveOffersFirstPage.length === 0) {
        console.log('⏳ Exclusive offers not loaded yet, will retry restoration');
        setTimeout(() => {
          this.tryRestoreExclusiveOfferPosition(context);
        }, 500);
        return;
      }
      
      // Restore to the exclusive offer card
      this.$nextTick(() => {
        setTimeout(() => {
          const restored = safeProductListCache.restoreToProductCard(
            cacheKey,
            1, // Exclusive offers are always on page 1
            productId,
            500
          );
          
          if (restored) {
            console.log(`✅ Successfully restored to exclusive offer ${productId}`);
            this.clearStoredProductNavigationContext();
            // Re-enable package restoration after exclusive offer restoration completes
            setTimeout(() => {
              console.log('📦 Exclusive offer restoration complete, re-enabling package restoration');
            }, 1000);
          } else {
            console.log(`❌ Failed to restore exclusive offer position. Clearing context.`);
            this.clearStoredProductNavigationContext();
          }
        }, 500);
      });
    },

    restoreScrollPosition() {
      // Priority 1: Product card position restoration (highest priority)
      const productContext = this.getStoredProductNavigationContext();
      if (productContext && productContext.portalPage === 'store') {
        // Prevent package restoration during product/exclusive offer restoration
        if (safeProductListCache) {
          safeProductListCache.preventScrollInterference(5000); // 5 seconds
          safeProductListCache.disableAutoScrolling(5000);
        }
        
        // Use product card restoration system (handles both products and exclusive offers)
        this.$nextTick(() => {
          setTimeout(() => {
            this.tryRestoreProductPosition();
          }, 100);
        });
        return;
      }
      
      // Priority 2: Generic sessionStorage (legacy support)
      // Only use if no product context exists
      // Otherwise, let nuxt-scroll-restoration package handle it
      this.$nextTick(() => {
        setTimeout(() => {
          const storeUsername = this.storeUsername;
          if (storeUsername && process.client) {
            const savedScrollPosition = sessionStorage.getItem(`scrollPosition_${storeUsername}`);
            
            if (savedScrollPosition && !isNaN(savedScrollPosition)) {
              const scrollPosition = parseInt(savedScrollPosition);
              
              // Smooth scroll to the saved position
              window.scrollTo({
                top: scrollPosition,
                behavior: 'smooth'
              });
              
              // Clear the saved position after restoring
              sessionStorage.removeItem(`scrollPosition_${storeUsername}`);
            } else {
              // No legacy position - let nuxt-scroll-restoration package handle it
              console.log('📦 No legacy position, using nuxt-scroll-restoration package');
            }
          }
        }, 100); // Small delay to ensure DOM is fully rendered
      });
    },
  },

  created() {
    // takes the response from the server-side request channeled
    // through the setup function and sets it into the component store variables
    if (this.storeInitialResponse && !isEmpty(this.storeInitialResponse)) {
      // Check if it's an error response (store not found)
      if (this.storeInitialResponse.error) {
        // Set storeItem to null to trigger the "store not found" UI
        this.storeItem = null;
        console.log('Store not found:', this.storeInitialResponse.error);
      } else {
        this.setResponse({ response: this.storeInitialResponse });
        
        // No need to wait for PagePortal updates since data is already available on server-side
        // Meta tags are set during SSR, so crawlers will see them immediately
        
        // if the products are empty, fetch them
        const { list, searchValue = "", selectedCategoryId = "all" } = this.storeInitialResponse?.products || {};
        if (process.client) {
          const areProductsEmpty = (!list || list?.length < 1);
          if (areProductsEmpty) {
            this.getAll({searchValue, selectedCategoryId});
          }
        }
      }
    }
  },
  mounted() {
    if (process.client){
      fbq('track', 'ViewContent');
      this.baseUrl = window.location.origin;

      this.pageLoading = false;
      
      // Initialize product cache system
      this.safeProductListCache = safeProductListCache;
      
      // Set up scroll position saving when products are loaded
      this.$nextTick(() => {
        if (this.storeProducts && this.storeProducts.length > 0) {
          const currentFilters = this.getCurrentFilters();
          const cacheKey = safeProductListCache.generateCacheKey(
            currentFilters,
            this.selectedSortBy || "all",
            this.selectedProductTypeId !== 'all' ? { value: this.selectedProductTypeId } : null
          );
          const currentPage = this.storeItem?.products?.currentPage || 1;
          safeProductListCache.setupScrollSaving(cacheKey, currentPage);
        }
      });
      
      // Restore scroll position when returning from product page
      this.restoreScrollPosition();
    };
  },
  beforeUnmount() {
    // Remove scroll saving
    if (this.safeProductListCache) {
      safeProductListCache.removeScrollSaving();
    }
  },

};
</script>

<style lang="scss">
@media (min-width: 640px) {
  #storePage {
    .store_categories {
      width: 30%;
    }
  }
}

#storePage {
  .store__inner--container {
    -webkit-backdrop-filter: saturate(110%) blur(15px) brightness(92%);
    backdrop-filter: saturate(110%) blur(15px) brightness(92%);
  }
  .store_categories {
    width: 40%;
    min-width: 150px;
  }
  @media (max-width: 640px) {
    .store_categories {
      width: 100%;
    }
  }
  #productType{
    input[type="radio"] {
      appearance: none;
      -webkit-appearance: none;
      width: calc(10px*1.4);
      height: calc(10px*1.4);
      border: 1px solid #c9c9c9;
      border-radius: 50%;
      background-color: transparent;
      position: relative;
      cursor: pointer;
    }

    input[type="radio"]:checked::before {
      content: "";
      display: block;
      width: calc(6px*1.3);
      height: calc(6px*1.3);
      background-color: #001eff;
      border-radius: 50%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      animation: appear 0.2s;
    }
  }

}

/* Enhanced Custom scrollbar styling */
.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Enhanced Radio button styling */
.form-radio {
  appearance: none;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border: 1.5px solid #c9c9c9;
  border-radius: 50%;
  background-color: transparent;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-radio:checked {
  border-color: var(--primary-color, #001eff);
  border-width: 1.5px;
}

.form-radio:checked::before {
  content: "";
  display: block;
  width: 8px;
  height: 8px;
  background-color: var(--primary-color, #001eff);
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: appear 0.2s;
}

.form-radio:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 30, 255, 0.2);
}

@keyframes appear {
  from {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
}

@media (min-height: 900px) {
  .custom-scrollbar.filter-sidebar {
    max-height: calc(100vh - 160px);
  }
}

@media (max-height: 700px) {
  .custom-scrollbar.filter-sidebar {
    max-height: calc(100vh - 120px);
  }
}
</style>
